import type {
  ClientRecordNotice,
  ClientRecordNoticeType,
} from '@/lib/clients/client-record-zone-d-notices';
import { clientWorkAreaForTab } from '@/lib/clients/client-workstation-contract';
import type { ClientRecordTabId } from '@/lib/clients/resolve-initial-client-tab';

/**
 * Zone D first paint (D1 over D3 force-open): collapsed is always chip-only.
 * Required notices stay indicated on the chip + an sr-only alert. Expanding
 * reveals the stack; do not mount a Card row on first paint.
 */
export function selectZoneDVisibleNotices(input: {
  notices: readonly ClientRecordNotice[];
  userExpanded: boolean;
}): ClientRecordNotice[] {
  if (!input.userExpanded) return [];
  return [...input.notices];
}

export function formatZoneDChipLabel(input: {
  noticeCount: number;
  requiredCount: number;
}): string {
  if (input.noticeCount === 0) return 'All clear';
  const notices = `${input.noticeCount} notice${input.noticeCount === 1 ? '' : 's'}`;
  if (input.requiredCount <= 0) return notices;
  return `${input.requiredCount} required · ${notices}`;
}

/**
 * KEEP the notice chip on Home / Journey (summary). Work tabs still mount
 * Zone D below the body so SIRS and other safety notices remain reachable.
 */
export function shouldPaintZoneDChipFirstPaint(tabId?: string | null): boolean {
  const tab = String(tabId ?? '').trim();
  if (!tab) return true;
  return clientWorkAreaForTab(tab as ClientRecordTabId) === 'summary';
}

/**
 * Types Zone D must not paint. Name the type and say where the coordinator
 * still reaches it. Adding a key here is how you exclude a type; falling off
 * an allowlist is not. Dedupe already owns overlap with Zone B.
 */
export const ZONE_D_NOTICE_TYPES_OWNED_ELSEWHERE = {
  clinical_risk_flag:
    'Standing clinical risk is a Clinical-tab fact (Edit clinical facts). A Zone D banner put that clinical fact on People.',
} as const satisfies Partial<Record<ClientRecordNoticeType, string>>;

export function isZoneDPaintedNotice(notice: ClientRecordNotice): boolean {
  return !(notice.type in ZONE_D_NOTICE_TYPES_OWNED_ELSEWHERE);
}

/** Paint every notice that survived action-queue dedupe, minus named exclusions. */
export function selectZoneDPaintedNotices(
  notices: readonly ClientRecordNotice[],
): ClientRecordNotice[] {
  return notices.filter(isZoneDPaintedNotice);
}

export function formatBudgetVintageMismatchDetail(input: {
  field: 'annual' | 'quarterly' | string;
  stored: number;
  canonical: number;
  resolutionLabel: string;
  /** Schedule the canonical figure comes from, e.g. "1 October 2026". */
  canonicalScheduleLabel?: string | null;
  /** Schedule the stored figure matches, when it matches a published one. */
  storedScheduleLabel?: string | null;
}): string {
  const stored = input.stored.toLocaleString('en-AU', { minimumFractionDigits: 2 });
  const canonical = input.canonical.toLocaleString('en-AU', { minimumFractionDigits: 2 });
  const period = input.field === 'annual' ? 'year' : 'quarter';
  // "still" only when the stored figure is a superseded published amount.
  const storedClause = input.storedScheduleLabel
    ? `still $${stored} (${input.storedScheduleLabel} schedule)`
    : `is $${stored} (not a published schedule amount)`;
  const from = input.canonicalScheduleLabel ? ` from ${input.canonicalScheduleLabel}` : '';
  return `Stored ${period} ${storedClause}. ${input.resolutionLabel}${from} is $${canonical}. Reconcile the envelope before you rely on remaining.`;
}
